import React from "react";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { NextSeo, LocalBusinessJsonLd } from "next-seo";
import getConfig from "next/config";
import { PageLayout } from "@themuse/design-system";
import { Culture, EmployeeStory, ORMSingleCompany } from "interfaces/api/apiInterfaces";
import { AppProvider } from "hooks/AppContext";
import { UserProvider } from "hooks/UserContext";
import { FeatureFlagsProvider } from "hooks/FeatureFlagsContext";
import { SnowPlowInit } from "utils/tracking";
import { UserInit } from "utils/user";
import { getActiveFeatureFlags } from "utils/featureFlags";
import { getCulturesList, getEmployeeStories, getSingleCompany } from "utils/api";
import { CompanyPage } from "components/CompanyPage";
import styles from "styles/Home.module.scss";

interface InitialAppParams {
    company: ORMSingleCompany;
    cultures?: Culture[];
    employeeStories?: EmployeeStory[];
    cultureId?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const { companyShortName } = context?.query as NodeJS.Dict<string>; // eslint-disable-line
    let notFound = false;
    const [company, cultures, employeeStories] = await Promise.allSettled([
        getSingleCompany({ companyShortName }),
        getCulturesList({ companyShortName }),
        getEmployeeStories({
            companyShortName,
            culture: String(context.query?.culture || "all"),
        }),
    ]).then((results) => results.map(({ status, value }: { status: string; value?: any }) => (
        status === "fulfilled" ? value : undefined
    )));

    // This profile can never exist.
    // https://nextjs.org/docs/api-reference/data-fetching/get-server-side-props#notfound
    const failRules = [
        !company?.id,
        context.req.url === "/profiles/null",
        (/\/profiles\/[^\w]/i).test(context.req.url),
        (/^http/i).test(context.req.url)
    ];

    if (failRules.some(Boolean)) {
        notFound = true;
    }

    const initialAppParams: InitialAppParams = {
        company,
        cultures,
        employeeStories,
        cultureId: "test",
    };

    return {
        notFound,
        props: {
            statusCode: context.res.statusCode,
            initialAppParams,
            featureFlags: {
                ...getActiveFeatureFlags(context)
            }
        }
    };
};

const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const { initialAppParams, featureFlags } = props;
    const { company } = initialAppParams;
    const { publicRuntimeConfig: { museWebHost } } = getConfig();
    const pageLayoutProps: React.ComponentProps<typeof PageLayout> = {
        activePath: "/companies",
        headerClassName: styles.pageHeader,
        pageName: styles.pageWrapper,
        mainClassName: styles.main,
        enableCompanySearch: true,
    };

    return (
        <FeatureFlagsProvider featureFlags={featureFlags}>
            <AppProvider {...initialAppParams}>
                <NextSeo
                    title={`${company.name} Jobs and Company Culture | The Muse`}
                    description={company.description}
                    canonical={`${museWebHost}/profiles/${company.short_name}`}
                    openGraph={{
                        url: `${museWebHost}/profiles/${company.short_name}`,
                        title: `${company.name} Jobs and Company Culture | The Muse`,
                        description: company.description,
                        siteName: "The Muse",
                        images: [
                            {
                                url: "https://www.themuse.com/static/images/muse-og-image.png",
                                width: 400,
                                height: 400,
                                alt: "The Muse Logo",
                                type: "image/jpeg",
                            }
                        ]
                    }}
                    additionalMetaTags={[
                        {
                            property: "msvalidate.01",
                            content: "4115DAD7B842017BA77853D5654B5CB0"
                        },
                        {
                            name: "p:domain_verify",
                            content: "09380361cb040eec0860a103a9c11da2"
                        }
                    ]}
                    twitter={{
                        handle: "@TheMuse",
                        site: "@TheMuse",
                        cardType: "summary_large_image",
                    }}
                />
                <LocalBusinessJsonLd
                    type="LocalBusiness"
                    id={`${museWebHost}/profiles/${company.short_name}`}
                    name={company.name}
                    description={company.description}
                    url={`${museWebHost}/profiles/${company.short_name}`}
                    address={null}
                    images={[
                        company.snapshot_photo,
                        company.logo,
                    ]}
                    numberOfEmployees={company?.employee_range?.split("_")?.shift() ?? "unknown"}
                />
                <UserProvider>
                    <UserInit>
                        <SnowPlowInit />
                        <CompanyPage {...pageLayoutProps} />
                    </UserInit>
                </UserProvider>
            </AppProvider>
        </FeatureFlagsProvider>
    );
};

export default Home;
